import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
} from '../components/icons';
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { requestPasswordReset } from '../services/authService';
import { getErrorMessage } from '../utils/errorUtils';

// Styled TextField with modern design
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    border: '2px solid transparent',
    transition: 'all 0.3s ease',
    '& fieldset': {
      border: '2px solid #e2e8f0',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      backgroundColor: '#f1f5f9',
      '& fieldset': {
        borderColor: '#cbd5e1',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
      '& fieldset': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
    '&.Mui-disabled': {
      backgroundColor: '#f1f5f9',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#667eea',
      fontWeight: 600,
    },
  },
}));

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await requestPasswordReset(email);

      setSuccess(true);
    } catch (error) {
      console.error('Password reset request error:', error);
      const errorMessage = getErrorMessage(error, 'Failed to send password reset email. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.3,
        },
      }}
    >
      {/* Back to Login Button */}
      <Box sx={{ position: 'absolute', top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 }, zIndex: 10 }}>
        <Button
          onClick={() => navigate('/login')}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.2 },
            borderRadius: '12px',
            textTransform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              transform: 'translateX(-3px)',
            },
          }}
        >
          Back to Login
        </Button>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', pt: { xs: 10, sm: 8 }, pb: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
        <div style={{ width: '100%' }}>
          <Card
            sx={{
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              mt: { xs: 6, sm: 0 },
            }}
          >
            <Box sx={{ p: { xs: 4, sm: 6 } }}>
              {/* Logo and Brand */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <div>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    }}
                  >
                    <EmailIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                </div>

                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    color: '#1f2937',
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                  }}
                >
                  Forgot Password?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#6b7280',
                    fontSize: '1rem',
                  }}
                >
                  {success
                    ? 'Check your email for reset instructions'
                    : 'Enter your email to receive reset instructions'
                  }
                </Typography>
              </Box>

              {success ? (
                <div>
                  <Alert
                    severity="success"
                    sx={{
                      mb: 3,
                      borderRadius: '12px',
                      '& .MuiAlert-icon': {
                        fontSize: 24,
                      },
                    }}
                  >
                    If an account exists with this email, you will receive password reset instructions shortly. Please check your inbox and spam folder.
                  </Alert>

                  <Button
                    component={RouterLink}
                    to="/login"
                    fullWidth
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      py: 1.8,
                      borderRadius: '12px',
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b3fa0 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                      },
                    }}
                  >
                    Back to Login
                  </Button>
                </div>
              ) : (
                <>
                  {error && (
                    <div>
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: '12px',
                          '& .MuiAlert-icon': {
                            fontSize: 24,
                          },
                        }}
                      >
                        {error}
                      </Alert>
                    </div>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <StyledTextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      sx={{ mb: 3 }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        py: 1.8,
                        borderRadius: '12px',
                        fontSize: '1.05rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a67d8 0%, #6b3fa0 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                        },
                        '&:disabled': {
                          background: 'linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%)',
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>

                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Remember your password?{' '}
                        <RouterLink
                          to="/login"
                          style={{
                            color: '#667eea',
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          Sign in
                        </RouterLink>
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                px: 4,
                backgroundColor: '#f9fafb',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} merchQRy. All rights reserved.
              </Typography>
            </Box>
          </Card>
        </div>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;

